import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquareReply, ThumbsUp, Quote, Trash2, Flag } from 'lucide-react';
import { useStore, isAdminPk, boardPkOf } from '@/relatos/store';
import { Avatar, Author, TimeAgo, OnlinePill } from './bits';
import { renderBBCode } from '@/lib/bbcode';
import { UploadButtons } from './upload-buttons';
import type { PostPayload } from '@/relatos/kinds';
import type { NostrEvent } from 'nostr-tools';

interface PostItem { ev: NostrEvent; payload: PostPayload; epoch: number }

function Toolbar({ onInsert, boardId }: { onInsert: (before: string, after?: string) => void; boardId: string }) {
  const items: [string, string, string][] = [
    ['B', '[b]', '[/b]'], ['I', '[i]', '[/i]'], ['U', '[u]', '[/u]'], ['S', '[s]', '[/s]'],
    ['Quote', '[quote]', '[/quote]'], ['Code', '[code]', '[/code]'],
    ['Link', '[url]', '[/url]'], ['Img', '[img]', '[/img]'],
  ];
  return (
    <div className="rl-toolbar" role="toolbar" aria-label="BBCode">
      {items.map(([label, b, a]) => (
        <button key={label} type="button" onClick={() => onInsert(b, a)}>{label}</button>
      ))}
      <UploadButtons onInsert={s => onInsert(s)} boardId={boardId} />
    </div>
  );
}

function Composer({ boardId, rootId, parentId, onDone, initial = '' }: {
  boardId: string; rootId: string; parentId: string; onDone: () => void; initial?: string;
}) {
  const { postReply } = useStore();
  const [body, setBody] = useState(initial);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const insert = (before: string, after = '') => {
    const ta = taRef.current;
    if (!ta) { setBody(b => b + before + after); return; }
    const { selectionStart: s, selectionEnd: e } = ta;
    const sel = body.slice(s, e);
    const next = body.slice(0, s) + before + sel + after + body.slice(e);
    setBody(next);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); });
  };

  const go = async () => {
    setBusy(true); setErr(null);
    try { await postReply(boardId, rootId, parentId, body); setBody(''); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  return (
    <div className="rl-panel">
      <Toolbar onInsert={insert} boardId={boardId} />
      {preview
        ? <div className="rl-post-body" style={{ border: '1px solid var(--rl-border)', background: '#fff', minHeight: 120 }}
               dangerouslySetInnerHTML={{ __html: renderBBCode(body) }} />
        : <textarea ref={taRef} className="rl-textarea" value={body} onChange={e => setBody(e.target.value)}
                    placeholder="Reply in BBCode…" aria-label="Reply body" />}
      <div className="rl-actions" style={{ marginTop: 8, marginBottom: 0 }}>
        <button className="rl-btn rl-btn-primary" disabled={busy || !body.trim()} onClick={go}>
          {busy ? 'Encrypting & posting…' : 'Post reply'}
        </button>
        <button className="rl-btn" onClick={() => setPreview(p => !p)}>{preview ? 'Edit' : 'Preview'}</button>
      </div>
      {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
    </div>
  );
}

function PostRow({ item, index, boardId, onQuote }: {
  item: PostItem; index: number; boardId: string; onQuote: (i: PostItem) => void;
}) {
  const { data, react, deletePost, reportPost, signer, boards } = useStore();
  const reactions = data[boardId]?.reactions[item.ev.id]?.length ?? 0;
  const joined = data[boardId]?.roster[item.ev.pubkey];
  const deletedBy = data[boardId]?.deleted[item.ev.id];
  const m = boards[boardId];
  // NIP-09: honor a deletion only from the post author or the board admin.
  // In-board pubkeys are per-board pseudos; legacy posts may carry identity keys.
  const me = boardPkOf(m) ?? signer?.pubkey ?? null;
  const isDeleted = !!deletedBy && (deletedBy === item.ev.pubkey || isAdminPk(m, deletedBy));
  const canDelete = !!signer && !!me && (me === item.ev.pubkey || signer.pubkey === item.ev.pubkey || isAdminPk(m, me) || m?.admin === signer.pubkey);
  const [confirming, setConfirming] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportMsg, setReportMsg] = useState<string | null>(null);

  const doReport = () => {
    const reason = window.prompt('Reason for reporting this post (optional):') ?? '';
    reportPost(boardId, { id: item.ev.id, pubkey: item.ev.pubkey }, reason,
               { subject: item.payload.subject, body: item.payload.body })
      .then(() => setReportMsg('Report sent — thank you.'))
      .catch(e => setReportMsg(e instanceof Error ? e.message : String(e)));
    setReporting(false);
  };

  return (
    <article className="rl-post" id={`p${item.ev.id.slice(0, 8)}`}>
      <div className="rl-post-side">
        <Avatar pk={item.ev.pubkey} />
        <Author pk={item.ev.pubkey} />
        {joined && <div className="rl-joined">Member since {new Date(joined * 1000).toLocaleDateString()}</div>}
      </div>
      <div className="rl-post-main">
        <div className="rl-post-head">
          <span><strong>{index === 0 ? (item.payload.subject ?? '') : `Re: ${item.payload.subject ?? ''}`}</strong></span>
          <span><TimeAgo ts={item.ev.created_at} /> · #{index + 1}
            {m && item.epoch !== m.currentEpoch &&
              <span className="rl-pill" style={{ marginLeft: 4 }}>epoch {item.epoch}</span>}
          </span>
        </div>
        {isDeleted ? (
          <div className="rl-post-body rl-small" style={{ fontStyle: 'italic', opacity: 0.6 }}>
            This post was deleted{isAdminPk(m, deletedBy ?? '') && deletedBy !== item.ev.pubkey ? ' by the board admin' : ''}.
          </div>
        ) : (
          <div className="rl-post-body" dangerouslySetInnerHTML={{ __html: renderBBCode(item.payload.body) }} />
        )}
        <div className="rl-post-foot">
          {signer && !isDeleted && (
            <>
              <button className="rl-btn rl-btn-sm" onClick={() => onQuote(item)} aria-label="Quote this post">
                <Quote size={11} aria-hidden style={{ verticalAlign: -1 }} /> Quote
              </button>
              <button className="rl-btn rl-btn-sm" onClick={() => react(boardId, item.ev.id)} aria-label="Like this post">
                <ThumbsUp size={11} aria-hidden style={{ verticalAlign: -1 }} /> {reactions > 0 ? `+${reactions}` : 'Like'}
              </button>
              {canDelete && !confirming && (
                <button className="rl-btn rl-btn-sm rl-btn-danger" onClick={() => setConfirming(true)} aria-label="Delete this post">
                  <Trash2 size={11} aria-hidden style={{ verticalAlign: -1 }} /> Delete
                </button>
              )}
              {canDelete && confirming && (
                <>
                  <button className="rl-btn rl-btn-sm rl-btn-danger" onClick={() => { deletePost(boardId, item.ev.id).catch(() => undefined); setConfirming(false); }}>
                    Confirm delete
                  </button>
                  <button className="rl-btn rl-btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
                </>
              )}
              {!reporting && !reportMsg && (
                <button className="rl-btn rl-btn-sm" onClick={() => setReporting(true)} aria-label="Report this post">
                  <Flag size={11} aria-hidden style={{ verticalAlign: -1 }} /> Report
                </button>
              )}
              {reporting && (
                <>
                  <button className="rl-btn rl-btn-sm rl-btn-danger" onClick={doReport}>Confirm report</button>
                  <button className="rl-btn rl-btn-sm" onClick={() => setReporting(false)}>Cancel</button>
                </>
              )}
              {reportMsg && <span className="rl-small" style={{ marginLeft: 4 }}>{reportMsg}</span>}
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export default function ThreadView({ boardId, threadId }: { boardId: string; threadId: string }) {
  const { boards, data, profiles, ensureProfiles, signer } = useStore();
  const m = boards[boardId];
  const d = data[boardId];
  const [quote, setQuote] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const composerAnchor = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const posts = d?.posts ?? [];
    const root = posts.find(p => p.ev.id === threadId && p.payload.kind === 'thread');
    if (!root) return null;
    const replies = posts
      .filter(p => p.payload.kind === 'reply' && p.payload.root === threadId)
      .sort((a, b) => a.ev.created_at - b.ev.created_at)
      .map(p => ({ ...p, payload: { ...p.payload, subject: root.payload.subject } }));
    return [{ ...root }, ...replies] as PostItem[];
  }, [d?.posts, threadId]);

  useEffect(() => {
    if (items) ensureProfiles(items.map(i => i.ev.pubkey));
  }, [items, ensureProfiles]);

  const onQuote = (item: PostItem) => {
    const name = profiles[item.ev.pubkey]?.name ?? 'someone';
    setQuote(`[quote="${name}"]${item.payload.body}[/quote]\n`);
    setShowComposer(true);
    requestAnimationFrame(() => composerAnchor.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  if (!m) return <div className="rl-panel rl-empty">You don’t hold a key for this board.</div>;
  if (!items) return <div className="rl-panel rl-empty">{d?.loading ? 'Fetching and decrypting…' : 'Thread not found.'}</div>;

  const subject = items[0].payload.subject ?? '(no subject)';

  return (
    <div>
      <div className="rl-actions" style={{ justifyContent: 'space-between' }}>
        <button className="rl-btn rl-btn-primary" disabled={!signer} onClick={() => setShowComposer(s => !s)}>
          <MessageSquareReply size={12} aria-hidden style={{ verticalAlign: -2 }} /> Post reply
        </button>
        <OnlinePill online={d?.online ?? {}} />
      </div>

      <div className="rl-cat"><span>{subject}</span></div>
      <div>
        {items.map((item, i) => (
          <PostRow key={item.ev.id} item={item} index={i} boardId={boardId} onQuote={onQuote} />
        ))}
      </div>

      <div ref={composerAnchor} style={{ marginTop: 14 }}>
        {signer && showComposer && (
          <Composer boardId={boardId} rootId={threadId} parentId={threadId}
                    initial={quote} onDone={() => { setQuote(''); setShowComposer(false); }} />
        )}
      </div>
    </div>
  );
}
